// Copyright (C) 2021 Intel Corporation
//
// SPDX-License-Identifier: MIT
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import notification from 'antd/lib/notification';
import Typography from 'antd/lib/typography';
import Table, { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import Button from 'antd/lib/button';
import { Link } from 'react-router-dom';
import { SorterResult } from 'antd/lib/table/interface';
import { fetchUserActivityStats, UserActivityStats } from '../../utils/activity';

interface TableData {
    key: number;
    id: number;
    label: string;
    totalScore: number;
    avgScore: number;
    activityCount: number;
}

function ActivityListPage(): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<TableData[]>([]);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
    });
    const [sortField, setSortField] = useState<string>('total_score');
    const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend');

    const columns = useMemo<ColumnsType<TableData>>(
        () => [
            {
                title: '# User',
                dataIndex: 'id',
                key: 'id',
                sorter: true,
                sortOrder: sortField === 'user_id' ? sortOrder : undefined,
            },
            {
                title: 'Label',
                dataIndex: 'label',
                key: 'label',
            },
            {
                title: 'Activity Count',
                dataIndex: 'activityCount',
                key: 'activityCount',
                sorter: true,
                sortOrder: sortField === 'activity_count' ? sortOrder : undefined,
            },
            {
                title: 'Score',
                dataIndex: 'totalScore',
                key: 'totalScore',
                sorter: true,
                sortOrder: sortField === 'total_score' ? sortOrder : undefined,
                defaultSortOrder: 'descend',
            },
            {
                title: 'Average Score',
                dataIndex: 'avgScore',
                key: 'avgScore',
                render: (value: number) => value.toFixed(2),
            },
            {
                title: 'Options',
                key: 'options',
                render: (_: string, record: TableData) => (
                    <Link to={`/activity/${record.id}`}>
                        <Button block>View</Button>
                    </Link>
                ),
            },
        ],
        [sortField, sortOrder],
    );

    const load = useCallback(async (page = 1, pageSize = 10, ordering = '-total_score') => {
        setLoading(true);
        try {
            const response = await fetchUserActivityStats(page, pageSize, ordering);

            const tableData: TableData[] = response.data.map((stat: UserActivityStats) => {
                const { user } = stat;
                const label =
                    `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || user?.username || 'Unknown';

                return {
                    key: stat.user_id,
                    id: stat.user_id,
                    label,
                    totalScore: stat.total_score,
                    avgScore: stat.avg_score,
                    activityCount: stat.activity_count,
                };
            });

            setData(tableData);
            setPagination((prev) => ({
                ...prev,
                current: response.page,
                pageSize: response.pageSize,
                total: response.count,
            }));
        } catch (e) {
            console.error(e);
            notification.error({
                message: 'Failed to load activities',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    const handleTableChange = useCallback(
        (
            newPagination: TablePaginationConfig,
            _filters: Record<string, any>,
            sorter: SorterResult<TableData> | SorterResult<TableData>[],
        ) => {
            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

            // Map column keys to API field names
            const fieldMap: Record<string, string> = {
                id: 'user_id',
                totalScore: 'total_score',
                activityCount: 'activity_count',
            };

            // Default sort values
            const defaultSortField = 'total_score';
            const defaultSortOrder: 'ascend' | 'descend' = 'descend';

            let newSortField: string;
            let newSortOrder: 'ascend' | 'descend';

            if (singleSorter.columnKey && singleSorter.order) {
                // Valid sort: use the selected column and order
                newSortField = fieldMap[singleSorter.columnKey as string] || defaultSortField;
                newSortOrder = singleSorter.order;
            } else {
                // Sorting cleared or non-sortable column clicked: reset to default
                newSortField = defaultSortField;
                newSortOrder = defaultSortOrder;
            }

            setSortField(newSortField);
            setSortOrder(newSortOrder);

            const ordering = newSortOrder === 'ascend' ? newSortField : `-${newSortField}`;

            load(newPagination.current || 1, newPagination.pageSize || 10, ordering);
        },
        [load],
    );

    useEffect(() => {
        load(1, 10, '-total_score');
    }, [load]);

    return (
        <div
            style={{
                width: '100%',
                maxWidth: 1140,
                padding: '30px 24px',
                margin: '0 auto',
            }}
        >
            <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                locale={{ emptyText: 'No activities found' }}
            />
        </div>
    );
}

export default ActivityListPage;
